import { createLazyResource } from '@magicdoorinc/ui';
import { createSignal, onCleanup, onMount } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { chatsSettingsRepository } from '~/repositories/settings/chatsSettingsRepository';
import type {
  ChatAILeaseSettings,
  ChatAILeaseSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  ChatAIMaintenanceRequestsSettings,
  ChatAIMaintenanceRequestsSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  ChatSettings,
  CommunicationsSettings,
  EmailSettings,
  TextSettings,
  TextSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

const MOBILE_VIEW_WITH = 1200;
const TABLET_VIEW_WITH = 1700;

export const [ChatsSettingsProvider, useChatsSettings] = createMagicDoorContext('ChatsSettings', () => {
  const repo = new chatsSettingsRepository();
  const [isMobileView, setIsMobileView] = createSignal(false);
  const [isTabletView, setIsTabletView] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);

  const [chatsSettings, chatsSettingActions] = createLazyResource<ChatSettings>(() => repo.getChatsSettings());

  const [allTextSettings, allTextSettingsActions] = createLazyResource<TextSettingsCompanyPortalTypedSettingsWithOverridesDocument>(() =>
    repo.getAllTextSettings()
  );

  const [allChatAILeaseSettings, allChatAILeaseSettingsActions] =
    createLazyResource<ChatAILeaseSettingsCompanyPortalTypedSettingsWithOverridesDocument>(() => repo.getAllChatAILeaseSettings());

  const [allChatAIMaintenanceRequestsSettings, allChatAIMaintenanceRequestsSettingsActions] =
    createLazyResource<ChatAIMaintenanceRequestsSettingsCompanyPortalTypedSettingsWithOverridesDocument>(() =>
      repo.getAllChatAIMaintenanceRequestsSettings()
    );

  const [emailSettings, emailSettingsActions] = createLazyResource<EmailSettings>(() => repo.getEmailSettings());

  const [communicationsSettings, communicationsSettingsActions] = createLazyResource<CommunicationsSettings>(() =>
    repo.getCommunicationsSettings()
  );

  const updateChatSettings = async (payload: ChatSettings) => {
    setIsLoading(true);
    try {
      await repo.changeChatSettings(payload);
      chatsSettingActions.refetch();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatsSettingsByLevel = async (level: string, levelId: string, payload: ChatSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.updateChatsSettingsByLevel(level, levelId, payload);
      chatsSettingActions.refetch();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChatsSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.deleteChatsSettingsByLevel(level, levelId);
      setTimeout(() => {
        chatsSettingActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // change update delete text setting like chats
  const updateTextSettingsByLevel = async (level: string, levelId: string, payload: TextSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.updateTextSettingsByLevel(level, levelId, payload);
      setTimeout(() => {
        allTextSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTextSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.deleteTextSettingsByLevel(level, levelId);
      setTimeout(() => {
        allTextSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTextSettings = async (payload: TextSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.changeTextSettings(payload);
      setTimeout(() => {
        allTextSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatAILeaseSettingsByLevel = async (level: string, levelId: string, payload: ChatAILeaseSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.updateChatAILeaseSettingsByLevel(level, levelId, payload);
      setTimeout(() => {
        allChatAILeaseSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChatAILeaseSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.deleteChatAILeaseSettingsByLevel(level, levelId);
      setTimeout(() => {
        allChatAILeaseSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatAILeaseSettings = async (payload: ChatAILeaseSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.changeChatAILeaseSettings(payload);
      setTimeout(() => {
        allChatAILeaseSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatAIMaintenanceRequestsSettingsByLevel = async (
    level: string,
    levelId: string,
    payload: ChatAIMaintenanceRequestsSettings
  ): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.updateChatAIMaintenanceRequestsSettingsByLevel(level, levelId, payload);
      setTimeout(() => {
        allChatAIMaintenanceRequestsSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChatAIMaintenanceRequestsSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.deleteChatAIMaintenanceRequestsSettingsByLevel(level, levelId);
      setTimeout(() => {
        allChatAIMaintenanceRequestsSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatAIMaintenanceRequestsSettings = async (payload: ChatAIMaintenanceRequestsSettings): Promise<void> => {
    setIsLoading(true);
    try {
      await repo.changeChatAIMaintenanceRequestsSettings(payload);
      setTimeout(() => {
        allChatAIMaintenanceRequestsSettingsActions.refetch();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateEmailSettings = async (payload: EmailSettings) => {
    setIsLoading(true);
    try {
      await repo.changeEmailSettings(payload);
      emailSettingsActions.refetch();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateCommunicationsSettings = async (payload: CommunicationsSettings) => {
    setIsLoading(true);
    try {
      await repo.changeCommunicationsSettings(payload);
      communicationsSettingsActions.refetch();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  onMount(() => {
    const resizeObserver = new ResizeObserver(() => {
      setIsMobileView(window.innerWidth <= MOBILE_VIEW_WITH);
      setIsTabletView(window.innerWidth <= TABLET_VIEW_WITH);
    });

    resizeObserver.observe(document.body);
    onCleanup(() => resizeObserver.disconnect());
  });

  return {
    isMobileView,
    isTabletView,
    isLoading,
    error,
    updateChatSettings,
    chatsSettings,
    updateChatsSettingsByLevel,
    deleteChatsSettingsByLevel,
    updateTextSettings,
    allTextSettings,
    updateTextSettingsByLevel,
    deleteTextSettingsByLevel,

    updateChatAILeaseSettings,
    allChatAILeaseSettings,
    updateChatAILeaseSettingsByLevel,
    deleteChatAILeaseSettingsByLevel,

    updateChatAIMaintenanceRequestsSettings,
    allChatAIMaintenanceRequestsSettings,
    updateChatAIMaintenanceRequestsSettingsByLevel,
    deleteChatAIMaintenanceRequestsSettingsByLevel,

    emailSettings,
    updateEmailSettings,

    communicationsSettings,
    updateCommunicationsSettings,
  };
});
